/**
 * byo-v4-sheets.js — the Sheet chrome, What's Inside and Help (P4).
 *
 * Ported by name from `byo-stages-v3.jsx`: `Sheet` (212), `HelpSheet` (168),
 * `BoxItemRow` (343), `InsideSheet` (403) and the nine `FAQS` (161).
 * DESIGN-SPEC.md §3.6 and §3.7.
 *
 * The Help sheet's questions come from the theme editor (CONFIG-SCHEMA.md §4)
 * so the page band and the sheet stay one source of truth. When no FAQ block is
 * configured it falls back to the design's nine defaults, which is what
 * DESIGN-SPEC.md §3.7 means by "the nine defaults are in byo-stages-v3.jsx
 * FAQS" — the sheet is never empty.
 *
 * A merchant answer is richtext from the editor, so it renders through
 * `dangerouslySetInnerHTML`; the built-in defaults are vnodes. `answerOf()` is
 * the one place that tells them apart.
 *
 * ES2020 module. View layer: assets/byo-v4-vendor.js (Preact + htm).
 */

import { html, useState, useEffect, useRef } from './byo-v4-vendor.js';
const { Icon, Btn, Pill, ProductImage, focusInto, focusBack } = (window.BYOV4 = window.BYOV4 || {});
const { QtyStepper } = (window.BYOV4 = window.BYOV4 || {});
const { TrayMeter } = (window.BYOV4 = window.BYOV4 || {});
/* Counts as words (D70), read at call time from byo-v4-core.js. */
const w = (n) => (window.BYO && window.BYO.words ? window.BYO.words(n) : String(n));
const W = (n) => (window.BYO && window.BYO.Words ? window.BYO.Words(n) : String(n));
/**
 * Sheet — byo-stages-v3.jsx:212. Scrim `rgba(43,26,34,.4)` over .3s; the panel
 * is 94% of the height on the phone and 92%/720px centred on desktop, sliding
 * on `transform .38s cubic-bezier(.2,.8,.2,1)`.
 */
export function Sheet({ open, onClose, title, eyebrow, display, children, z = 19 }) {
  const panel = useRef(null);
  const restoreTo = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    restoreTo.current = document.activeElement;

    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKey);

    const t = setTimeout(() => {
      const first = panel.current && panel.current.querySelector('button, [href], input, select, textarea');
      focusInto(panel.current, first);
    }, 60);

    return () => {
      window.removeEventListener('keydown', onKey);
      clearTimeout(t);
      focusBack(restoreTo.current);
      /* Safari never focuses a tapped button, so there may be nothing to give
         focus back to: don't leave it inside a closed sheet. */
      const p = panel.current;
      if (p && p.contains(document.activeElement)) document.activeElement.blur();
    };
  }, [open, onClose]);

  /* Drawn in place, not through Layer (D60): the sheet is position:absolute
     over the builder's screen frame, so it belongs inside that frame. Only
     position:fixed overlays were clipped by iPhone Safari. */
  return html`
    <div class=${`byo-v4-sheet${open ? ' is-open' : ''}`} style=${`z-index:${z}`}>
      <div class="byo-v4-sheet__scrim" onClick=${onClose}></div>
      <div
        class="byo-v4-sheet__panel"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
        aria-label=${typeof title === 'string' ? title : eyebrow || 'Sheet'}
        ref=${panel}
      >
        <div class="byo-v4-sheet__head">
          <div class="byo-v4-sheet__grab" aria-hidden="true"></div>
          <div class="byo-v4-sheet__headrow">
            <div class="byo-v4-sheet__title">
              ${eyebrow && html`<div class="byo-v4-sheet__eyebrow">${eyebrow}</div>`}
              <div class=${`byo-v4-sheet__h${display || eyebrow ? ' is-display' : ''}`}>${title}</div>
            </div>
            <button class="byo-v4-sheet__close" onClick=${onClose} aria-label="Close">
              <${Icon} name="close" size=${18} />
            </button>
          </div>
        </div>
        <div class="byo-v4-sheet__body">${children}</div>
      </div>
    </div>
  `;
}

/** A label/value row inside the ledger — byo-stages-v3.jsx:342. */
function Row({ children, class: cls = '' }) {
  return html`<div class=${`byo-v4-row ${cls}`.trim()}>${children}</div>`;
}

/**
 * BoxItemRow — byo-stages-v3.jsx:343. A 44px contained thumbnail, the title
 * clamped to two lines, the size/qty meta, the line price, then a 40px stepper
 * and a 40px outlined trash.
 */
export function BoxItemRow({ it, b }) {
  const p = b.byId[it.id];
  if (!p) return null;

  const meta = `${p.size}${b.itemsMode ? '' : ` · ${b.slotWord(p.size)}`}${
    it.qty > 1 ? ` × ${it.qty} · ${b.money(p.price)} each` : ''
  }`;

  return html`
    <div class="byo-v4-brow">
      <div class="byo-v4-brow__thumb"><${ProductImage} p=${p} fit="contain" /></div>
      <div class="byo-v4-brow__text">
        <div class="byo-v4-brow__title">${p.title}</div>
        <div class="byo-v4-brow__meta">${meta}</div>
      </div>
      <div class="byo-v4-brow__right">
        <span class="byo-v4-brow__price">${b.money(p.price * it.qty)}</span>
        <div class="byo-v4-brow__ctrls">
          <${QtyStepper} p=${p} b=${b} />
          <button class="byo-v4-brow__trash" onClick=${() => b.removeAll(p.id)} aria-label=${`Remove ${p.title}`} title="Remove from box">
            <${Icon} name="trash" size=${16} />
          </button>
        </div>
      </div>
    </div>
  `;
}

/**
 * InsideSheet — byo-stages-v3.jsx:403. DESIGN-SPEC.md §3.6.
 *
 * Items and the running total only: personalisation and the box choice belong
 * to their own steps, and there is deliberately no shortcut to the cart.
 */
export function InsideSheet({ b, config, open, onClose, onFit }) {
  const free = b.toFree <= 0;
  /* D66: under the threshold, the card offers the way to more. Only when
     something still fits: a full box is told to swap, not to add. D70: and
     only once the box has its minimum. Until then the nudge above is the one
     call to action, "Show what fits", so the sheet never offers two at once. */
  const room = !free && typeof b.fitsSize === 'function' && b.fitsSize('Small');
  const upsell = room && b.meetsMin;
  const n = (b.units || []).length;
  const pct = Math.min(100, b.freeShipping ? (b.shipTotal / b.freeShipping) * 100 : 0);
  /* The design says "free express delivery". Which speed goes free depends on
     the address, which the builder does not know yet (D62: Express to mainland
     UK, Standard to Northern Ireland), so the card says free delivery and no
     speed, and no saving in pounds either. */

  return html`
    <${Sheet} open=${open} onClose=${onClose} title="What's Inside">
      ${!b.meetsMin &&
      html`<div class="byo-v4-nudge" data-screen-label="Minimum nudge">
        <div class="byo-v4-nudge__text">
          <div class="byo-v4-nudge__h">
            ${b.itemsMode
              ? `Add ${w(b.need)} more to continue`
              : `${window.BYO.fmtSlots(b.need)} more space${b.need > 1 ? 's' : ''} to continue`}
          </div>
          <div class="byo-v4-nudge__sub">
            ${b.itemsMode
              ? b.itemCount
                ? `${W(b.itemCount)} of ${w(b.minItems)} items so far. Everything else is optional.`
                : 'No items yet. Everything else is optional.'
              : 'Then Personalise opens up.'}
          </div>
        </div>
        ${onFit && html`<${Btn} variant="white" size="sm" onClick=${onFit}>Show what fits<//>`}
      </div>`}

      <div class=${`byo-v4-freecard${free ? ' is-free' : ''}`} data-screen-label="Free delivery upsell">
        <div class="byo-v4-freecard__top">
          <span class="byo-v4-freecard__h">
            ${free ? 'Free shipping unlocked' : `${b.money(b.toFree)} more for free shipping`}
          </span>
          ${!free && html`<span class="byo-v4-freecard__of">${b.money(b.freeShipping)}+</span>`}
        </div>
        <div class="byo-v4-freecard__track">
          <div class="byo-v4-freecard__fill" style=${`width:${pct}%`}></div>
        </div>
        <div class="byo-v4-freecard__say">
          ${/* D70: under the minimum the nudge above is the only push, so this
               line only says where free shipping starts. */ ''}
          ${free
            ? 'This box ships free.'
            : !b.meetsMin
            ? `Shipping is free from ${window.BYO.moneyShort(b.freeShipping)}.`
            : room
            ? 'Add one or two more things and shipping is free.'
            : 'Your box is full. Swap something for a bigger treat to get there.'}
        </div>
        ${upsell &&
        onFit &&
        html`<${Btn} variant="secondary" size="sm" class="byo-v4-freecard__cta" onClick=${onFit}>What else fits in my box?<//>`}
      </div>

      ${/* D71: the box's slots, here too: the minimum still needed, then "+"
           for as long as more will fit, and one line to say so. */ ''}
      ${b.itemsMode
        ? html`<div class="byo-v4-inside__slots">
            <${TrayMeter} b=${b} height=${44} radius=${10} gap=${4} max=${7} />
            <div class="byo-v4-inside__slotsay">
              ${!b.meetsMin
                ? `${W(b.minItems)} minimum, then add as many as fit.`
                : b.remaining > 1e-9
                  ? 'Room for more · keep adding until your box is full.'
                  : 'Your box is full.'}
            </div>
          </div>`
        : html`<${TrayMeter} b=${b} height=${44} radius=${10} gap=${4} />`}

      <div class="byo-v4-inside__items">
        ${b.box.items.length === 0
          ? html`<div class="byo-v4-inside__empty">Nothing in here yet</div>`
          : b.box.items.map((it) => html`<${BoxItemRow} key=${it.id} it=${it} b=${b} />`)}
      </div>

      <div class="byo-v4-ledger">
        <${Row} class="byo-v4-row--top">
          <span>
            <span class="byo-v4-ledger__k">Gift wrapping service</span>
            <span class="byo-v4-ledger__note">Standard on every box</span>
          </span>
          <span class="byo-v4-ledger__k">${b.money(config.base || 0)}</span>
        <//>
        <${Row}><span>${n ? `${W(n)} item${n === 1 ? '' : 's'}` : 'No items'}</span><span>${b.money(b.itemsTotal)}</span><//>
        <${Row} class="byo-v4-row--total"><span>Total so far</span><span>${b.money(b.total)}</span><//>
      </div>
    <//>
  `;
}

/**
 * The design's nine defaults — byo-stages-v3.jsx:161. Used only when the theme
 * editor carries no FAQ blocks. Copy is verbatim, including the punctuation,
 * except the two delivery answers, which follow Shopify's delivery profile and
 * say what templates/page.byo-v4.json says (DEVIATIONS.md D56). The keepsake
 * box question is gone (D70): the box is offered only to a full box, and a
 * customer with a smaller one should not read about what they are missing.
 */
export const FAQS = [
  {
    q: 'When does it ship?',
    stage: 'review',
    a: html`<div>
      <p><strong>Express delivery - £5.25:</strong> 1–2 working days. Order before 12pm, Monday to Friday, to ship the same day.</p>
      <p><strong>Standard delivery – £3.50:</strong> 3–5 working days. Northern Ireland is Standard only, at £3.99.</p>
    </div>`,
  },
  {
    q: 'When is shipping free?',
    stage: 'review',
    a: 'Shipping is free from £60 per address, after any discount: by Standard, to mainland UK and to Northern Ireland. Past £60, Express to mainland UK costs just the difference, £1.75. Several boxes going to one address count together; boxes going to different addresses each need to reach £60 on their own. Under £60, pick Standard (£3.50) or Express (£5.25). Northern Ireland is Standard only, at £3.99.',
  },
  {
    q: 'Are there vegan and alcohol-free options?',
    stage: 'fill',
    a: 'Yes to both! Filter for vegan and booze-free treats as you build.',
  },
  {
    q: 'How do I add a photo and message?',
    stage: 'personal',
    a: "They're the second step in the builder. Upload the photo (and don't forget to hit submit!) then type your message. We will print the photo and handwrite the message to add to your gift before we send.",
  },
  {
    q: 'Can I send it straight to someone else?',
    stage: 'review',
    a: 'Yes. Enter their address at checkout and we’ll send it gift-wrapped with no prices inside. Sending several boxes to different people? Choose “Different addresses” in your cart and give each box its own recipient.',
  },
  {
    q: "Can I choose the day it's opened?",
    stage: 'personal',
    a: 'You can choose a save-the-date sticker, and we time the dispatch around it.',
  },
  {
    q: 'Will it fit through the letterbox?',
    stage: 'review',
    a: 'No - the boxes are too deep for a letterbox, so someone needs to be in.',
  },
  {
    q: 'Can I order several boxes for a team or event?',
    stage: 'review',
    a: html`<span
      >Yes! For larger orders, get in touch
      <a href="https://wraptgifts.com/pages/corporate-gifting" class="byo-v4-help__link">here.</a></span
    >`,
  },
];

/** Editor blocks win; the design's nine are the fallback. */
function faqsFrom(config) {
  const blocks = Array.isArray(config.faqs) ? config.faqs.filter((f) => f && f.q) : [];
  return blocks.length ? blocks : FAQS;
}

/** A merchant answer is richtext; a built-in one is already a vnode. */
function answerOf(f) {
  if (typeof f.a === 'string' && /<[a-z][\s\S]*>/i.test(f.a)) {
    return html`<div dangerouslySetInnerHTML=${{ __html: f.a }}></div>`;
  }
  return f.a;
}

/** Where "Chat" goes when there is no chat to open. */
const CONTACT_URL = 'https://wraptgifts.com/pages/get-in-touch';

/**
 * showChat — put the Shopify Inbox chat on screen, or say it could not.
 *
 * The Inbox app embed injects the storefront web component `<shopify-chat>`
 * (cdn.shopify.com/storefront/web-components/chat.js). It is dialog-shaped:
 * `show()` opens the panel and `close()` shuts it, while `open` is a boolean
 * property, not a method — `chat.open()` throws "is not a function". Measured
 * against Dev v5 on 21 Sep 2026; the panel is `position: fixed` at
 * z-index 2147483000, so it comes over our sheet rather than under it.
 *
 * The element is absent whenever the embed is off, and the store's consent
 * manager can hold the script back until cookies are accepted. So this reports
 * failure rather than swallowing the click, and the caller lets the link go to
 * the contact page as it always did.
 */
export function showChat() {
  const chat = document.querySelector('shopify-chat');
  if (!chat || typeof chat.show !== 'function') return false;
  try {
    chat.show();
  } catch (_) {
    return false;
  }
  return true;
}

/**
 * HelpSheet — byo-stages-v3.jsx:168. DESIGN-SPEC.md §3.7. The current stage's
 * questions come first, each tagged with a lime "This step" pill while
 * collapsed.
 */
export function HelpSheet({ b, config, open, stage = 'fill', onClose }) {
  const all = faqsFrom(config);
  const order = [...all.filter((f) => f.stage === stage), ...all.filter((f) => f.stage !== stage)];
  const [on, setOn] = useState(0);

  useEffect(() => {
    setOn(0);
  }, [stage, open]);

  return html`
    <${Sheet} open=${open} onClose=${onClose} display title="Questions, answered" z=${50}>
      <div class="byo-v4-help">
        ${order.map((f, i) => {
          const isOn = on === i;
          const rel = f.stage === stage;
          return html`
            <div class=${`byo-v4-help__row${i === order.length - 1 ? ' is-last' : ''}`} key=${f.q}>
              <button class="byo-v4-help__btn" onClick=${() => setOn(isOn ? -1 : i)} aria-expanded=${isOn}>
                <span class="byo-v4-help__q">${f.q}</span>
                ${rel && !isOn && html`<${Pill} tone="lime">This step<//>`}
                <span class=${`byo-v4-help__mark${isOn ? ' is-open' : ''}`} aria-hidden="true">
                  <${Icon} name="plus" size=${14} stroke=${2.5} />
                </span>
              </button>
              ${isOn && html`<div class="byo-v4-help__a">${answerOf(f)}</div>`}
            </div>
          `;
        })}

        <div class="byo-v4-help__footwrap">
          <div class="byo-v4-help__foot">
            <span class="byo-v4-help__stuck">Still stuck?</span>
            ${/* D70: "Chat", with a speech bubble, so it reads as the live chat
                 it opens rather than a contact form. */ ''}
            <a
              class="byo-v4-help__link byo-v4-help__chat"
              href=${CONTACT_URL}
              onClick=${(e) => {
                /* Only take the click when there is a chat to show. The sheet
                   closes behind it: the customer asked for a person, and a
                   scrim plus an FAQ list behind the panel reads as broken. */
                if (!showChat()) return;
                e.preventDefault();
                onClose && onClose();
              }}
              ><${Icon} name="chat" size=${18} />Chat</a
            >
          </div>
        </div>
      </div>
    <//>
  `;
}

/* Published for the siblings that used to `import` this file. See the
   section's loader: relative imports drop the ?v= and are served from a
   year-long CDN cache, so a sibling could be a build behind. */
Object.assign((window.BYOV4 = window.BYOV4 || {}), { BoxItemRow, FAQS, HelpSheet, InsideSheet, Sheet, showChat });
